import { useEffect, useRef, useState } from "react"
import QRCode from "qrcode"

export default function App() {
  const canvasRef = useRef(null)
  const [url, setUrl] = useState("")
  const [fgColor, setFgColor] = useState("#111111")
  const [bgColor, setBgColor] = useState("#ffffff")
  const [size, setSize] = useState(320)
  const [showLogo, setShowLogo] = useState(false)
  const [logoSrc, setLogoSrc] = useState("")
  const [logoScale, setLogoScale] = useState(0.22)
  const [logoRadius, setLogoRadius] = useState(16)
  const [logoBgColor, setLogoBgColor] = useState("#ffffff")

  const [showScanMe, setShowScanMe] = useState(false)
  const [scanText, setScanText] = useState("SCAN ME")
  const [scanBgColor, setScanBgColor] = useState("#2563eb")
  const [scanTextColor, setScanTextColor] = useState("#ffffff")
  const [scanFont, setScanFont] = useState(20)
  const [scanPosition, setScanPosition] = useState("top")

  const scale = typeof window !== "undefined" ? Math.max(1, Math.min(3, Math.floor(window.devicePixelRatio || 1))) : 2

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const draw = async () => {
      const real = Math.floor(size * scale)
      const padding = 8 * scale
      const gap = showScanMe ? 12 * scale : 0
      const bannerH = showScanMe ? Math.floor((scanFont * 2 + 20) * scale) : 0
      const totalH = showScanMe ? real + bannerH + gap : real

      canvas.width = real
      canvas.height = totalH
      canvas.style.width = size + "px"
      canvas.style.height = totalH / scale + "px"

      const qrCanvas = document.createElement("canvas")
      qrCanvas.width = real
      qrCanvas.height = real
      await QRCode.toCanvas(qrCanvas, url || "https://", {
        width: real,
        margin: 2 * scale,
        errorCorrectionLevel: "H",
        color: { dark: fgColor, light: bgColor }
      })

      const ctx = canvas.getContext("2d")
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      let qrY = 0
      if (showScanMe) {
        const y = scanPosition === "top" ? 0 : real + gap
        const x = padding
        const w = real - padding * 2
        const h = bannerH
        ctx.save()
        roundRect(ctx, x, y, w, h, 16 * scale)
        ctx.fillStyle = scanBgColor
        ctx.fill()
        ctx.fillStyle = scanTextColor
        ctx.font = `${Math.floor(scanFont * scale)}px system-ui, -apple-system, Segoe UI, Roboto`
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(scanText, x + w / 2, y + h / 2)
        ctx.restore()
        qrY = scanPosition === "top" ? bannerH + gap : 0
      }

      ctx.drawImage(qrCanvas, 0, qrY)

      if (showLogo && logoSrc) {
        const img = new Image()
        img.src = logoSrc
        await new Promise(r => (img.onload = r))
        const boxSize = Math.floor(real * logoScale)
        const x = Math.floor((real - boxSize) / 2)
        const y = Math.floor(qrY + (real - boxSize) / 2)
        const r = Math.min(logoRadius * scale, boxSize / 2)
        ctx.save()
        roundRect(ctx, x, y, boxSize, boxSize, r)
        ctx.clip()
        ctx.fillStyle = logoBgColor
        ctx.fillRect(x, y, boxSize, boxSize)
        ctx.drawImage(img, x, y, boxSize, boxSize)
        ctx.restore()
      }
    }

    draw()
  }, [url, fgColor, bgColor, size, showLogo, logoSrc, logoScale, logoRadius, logoBgColor, showScanMe, scanText, scanBgColor, scanTextColor, scanFont, scanPosition, scale])

  const handleLogoUpload = e => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => setLogoSrc(String(ev.target?.result || ""))
    reader.readAsDataURL(file)
  }

  const downloadPNG = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const link = document.createElement("a")
    link.download = "qr-code.png"
    link.href = canvas.toDataURL("image/png")
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const resetAll = () => {
    setUrl("")
    setFgColor("#111111")
    setBgColor("#ffffff")
    setSize(320)
    setShowLogo(false)
    setLogoSrc("")
    setLogoScale(0.22)
    setLogoRadius(16)
    setLogoBgColor("#ffffff")
    setShowScanMe(false)
    setScanText("SCAN ME")
    setScanBgColor("#2563eb")
    setScanTextColor("#ffffff")
    setScanFont(20)
    setScanPosition("top")
  }

  return (
    <div style={styles.page}>
      <style>{css}</style>
      <header style={styles.header}>
        <h1 style={styles.title}>easyQR</h1>
        <p style={styles.subtitle}>Generate QR from URL, tweak colors, add a logo, custom “Scan Me”, and download PNG</p>
      </header>
      <div className="grid">
        <section className="card preview">
          <div className="canvas-wrap"><canvas ref={canvasRef} /></div>
          <div className="actions">
            <button onClick={downloadPNG} className="btn primary">Download PNG</button>
            <button onClick={resetAll} className="btn">Reset</button>
          </div>
        </section>
        <section className="card controls">
          <div className="field">
            <label>URL</label>
            <input type="url" placeholder="https://example.com" value={url} onChange={e => setUrl(e.target.value)} />
          </div>
          <div className="row">
            <div className="field">
              <label>QR Color</label>
              <input type="color" value={fgColor} onChange={e => setFgColor(e.target.value)} />
            </div>
            <div className="field">
              <label>Background</label>
              <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Size: {size}px</label>
            <input type="range" min="200" max="600" step="10" value={size} onChange={e => setSize(parseInt(e.target.value))} />
          </div>
          <div className="switch">
            <input id="logo" type="checkbox" checked={showLogo} onChange={e => setShowLogo(e.target.checked)} />
            <label htmlFor="logo">Center Logo</label>
          </div>
          {showLogo && (
            <div className="logo-grid">
              <div className="field">
                <label>Upload logo</label>
                <input type="file" accept="image/*" onChange={handleLogoUpload} />
              </div>
              <div className="field">
                <label>Logo size: {(logoScale * 100).toFixed(0)}%</label>
                <input type="range" min="12" max="36" step="1" value={Math.round(logoScale * 100)} onChange={e => setLogoScale(parseInt(e.target.value) / 100)} />
              </div>
              <div className="field">
                <label>Logo radius: {logoRadius}px</label>
                <input type="range" min="0" max="48" step="2" value={logoRadius} onChange={e => setLogoRadius(parseInt(e.target.value))} />
              </div>
              <div className="field">
                <label>Logo background</label>
                <input type="color" value={logoBgColor} onChange={e => setLogoBgColor(e.target.value)} />
              </div>
            </div>
          )}

          <div className="switch">
            <input id="scanme" type="checkbox" checked={showScanMe} onChange={e => setShowScanMe(e.target.checked)} />
            <label htmlFor="scanme">Custom “Scan Me” banner</label>
          </div>

          {showScanMe && (
            <div className="logo-grid">
              <div className="field">
                <label>Banner text</label>
                <input type="text" placeholder="SCAN ME" value={scanText} onChange={e => setScanText(e.target.value)} />
              </div>
              <div className="row">
                <div className="field">
                  <label>Banner color</label>
                  <input type="color" value={scanBgColor} onChange={e => setScanBgColor(e.target.value)} />
                </div>
                <div className="field">
                  <label>Text color</label>
                  <input type="color" value={scanTextColor} onChange={e => setScanTextColor(e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <label>Font size: {scanFont}px</label>
                  <input type="range" min="12" max="36" step="1" value={scanFont} onChange={e => setScanFont(parseInt(e.target.value))} />
                </div>
                <div className="field">
                  <label>Position</label>
                  <select value={scanPosition} onChange={e => setScanPosition(e.target.value)}>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
      <footer style={styles.footer}>© 2025 Dion Mahmuti</footer>
    </div>
  )
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

const styles = {
  page: { minHeight: "100dvh", background: "#f8fafc", color: "#0f172a", padding: "24px" },
  header: { maxWidth: 1200, margin: "0 auto 16px auto" },
  title: { fontSize: 28, fontWeight: 700, letterSpacing: 0.4 },
  subtitle: { opacity: 0.8, marginTop: 6 },
  footer: { textAlign: "center", opacity: 0.7, marginTop: 24 }
}

const css = `
*{box-sizing:border-box}
.grid{max-width:1200px;margin:0 auto;display:grid;grid-template-columns:1.2fr 1fr;gap:16px}
.card{background:#ffffff;border:1px solid #e2e8f0;border-radius:16px;padding:16px}
.preview{display:flex;flex-direction:column;align-items:center;justify-content:center}
.canvas-wrap{display:flex;align-items:center;justify-content:center;min-height:420px}
.controls{display:grid;gap:14px}
.field{display:flex;flex-direction:column;gap:8px}
.field>label{font-size:12px;opacity:.8}
.field>input[type="url"], .field>input[type="file"], .field>input[type="text"], .field>select{background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:12px 14px;color:#0f172a;outline:none}
.field>input[type="url"]:focus, .field>input[type="text"]:focus, .field>select:focus{border-color:#2563eb;box-shadow:0 0 0 4px rgba(37,99,235,0.1)}
.field>input[type="range"]{width:100%}
.row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.switch{display:flex;align-items:center;gap:10px}
.logo-grid{display:grid;gap:12px}
.actions{display:flex;gap:10px;margin-top:16px}
.btn{background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:10px 14px;color:#0f172a}
.btn:hover{border-color:#2563eb}
.btn.primary{background:#2563eb;border-color:#2563eb;color:#ffffff}
.btn.primary:hover{filter:brightness(1.05)}
@media (max-width: 980px){.grid{grid-template-columns:1fr}}
`

//DION  MAHMUTI